/**
 * TrialBanner — sticky top strip that shows on every gameplay page for
 * pilots in the trial window. Nags gently ("6 days left · Buy for $2.99")
 * without blocking any UI. Auto-hides for paid, grandfathered, or
 * unauthenticated pilots.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Rocket, Lock } from "lucide-react";
import useGameAccess from "../lib/useGameAccess";

export default function TrialBanner() {
  const { snapshot } = useGameAccess();
  if (!snapshot) return null;
  if (["paid", "grandfathered", "needs_trial"].includes(snapshot.access)) return null;

  const isExpired = snapshot.access === "expired";
  const days = snapshot.days_left ?? 0;

  return (
    <div
      className={`w-full py-1.5 px-3 text-center border-b ${
        isExpired
          ? "bg-[#ff007f]/[0.12] border-[#ff007f]/40"
          : "bg-[#00ffff]/[0.08] border-[#00ffff]/30"
      }`}
      data-testid="trial-banner"
    >
      <div className="flex items-center justify-center gap-2 flex-wrap">
        {isExpired ? (
          <Lock className="w-3 h-3 text-[#ff007f]" />
        ) : (
          <Rocket className="w-3 h-3 text-[#00ffff]" />
        )}
        <span
          className={`font-mono-cosmic text-[10px] tracking-[0.25em] uppercase ${
            isExpired ? "text-[#ff007f]" : "text-[#00ffff]"
          }`}
        >
          {isExpired
            ? "Trial ended — buy full game to keep playing"
            : `Trial · ${days} day${days === 1 ? "" : "s"} left`}
        </span>
        <Link
          to="/unlock"
          className={`ml-2 px-2 py-0.5 border font-mono-cosmic text-[10px] tracking-[0.25em] uppercase transition ${
            isExpired
              ? "border-[#ffd700] text-[#ffd700] hover:bg-[#ffd700]/10"
              : "border-white/30 text-white hover:border-white/60"
          }`}
          data-testid="trial-banner-cta"
        >
          Buy · ${snapshot.price_usd ?? 2.99}
        </Link>
      </div>
    </div>
  );
}
